/**
 * LlmProfileEditor — sprava LLM profilu pomoci generickeho PriorityList.
 *
 * Wrapper nad PriorityList<LlmProfile>: dodava LLM-specificke veci
 * (preset picker, model discovery, token usage badge, API key modal).
 * Drag&drop, toggle, expand/collapse, add/delete resi PriorityList.
 *
 * Pouziti:
 *   <LlmProfileEditor
 *     profiles={profiles}
 *     onSave={save} onDelete={del} onToggle={toggle} onReorder={reorder}
 *     usage={usageMap} loadModels={fetchModels}
 *   />
 */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Eye, EyeOff } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from './Button';
import { PriorityList } from './PriorityList';
import { Select } from './Select';

// ── Typy ──────────────────────────────────────────────────────────────────────

export interface LlmProfile {
  id: string;
  label: string;
  url: string;
  model: string;
  api_key: string;
  backend: 'openai' | 'anthropic';
  enabled: boolean;
  priority: number;
  daily_token_limit: number;
  reasoning_enabled?: boolean;
  /** 'shared' = z hlavního llm-profiles registru — read-only tady, uprav v LLM Profiles modulu. */
  source?: 'own' | 'shared';
}

export interface BackendPreset {
  id: string;
  label: string;
  url: string;
  model: string;
  backend: 'openai' | 'anthropic';
}

export interface ProfileUsage {
  today: number;
  limit: number;
}

// ── Default presety ───────────────────────────────────────────────────────────

export const DEFAULT_PRESETS: BackendPreset[] = [
  {
    id: 'gb10-qwen3',
    label: 'GB10 Qwen3-Coder-30B',
    url: 'https://10.118.200.18:8004/v1',
    model: 'qwen3-coder-30b',
    backend: 'openai',
  },
  {
    id: 'gb10-vl7b',
    label: 'GB10 Qwen2.5-VL-7B',
    url: 'https://10.118.200.18:8005/v1',
    model: 'qwen2.5-vl-7b',
    backend: 'openai',
  },
  {
    id: 'ai2-ollama',
    label: 'ai2 Ollama qwen2.5-coder',
    url: 'https://10.118.200.19:11434/v1',
    model: 'qwen2.5-coder:14b',
    backend: 'openai',
  },
  {
    id: 'anthropic',
    label: 'Claude (Anthropic)',
    url: 'https://api.anthropic.com',
    model: 'claude-sonnet-4-6',
    backend: 'anthropic',
  },
  {
    id: 'openai',
    label: 'OpenAI GPT-4o',
    url: 'https://api.openai.com/v1',
    model: 'gpt-4o',
    backend: 'openai',
  },
  {
    id: 'kimi',
    label: 'Kimi (Moonshot)',
    url: 'https://api.moonshot.cn/v1',
    model: 'kimi-k2-0711-preview',
    backend: 'openai',
  },
  { id: 'custom', label: 'Custom...', url: '', model: '', backend: 'openai' },
];

// ── Props ─────────────────────────────────────────────────────────────────────

interface LlmProfileEditorProps {
  profiles: LlmProfile[];
  onSave: (profile: LlmProfile) => void;
  onDelete: (id: string) => void;
  onToggle: (id: string, enabled: boolean) => void;
  onReorder: (orderedIds: string[]) => void;
  onAdd?: () => void;
  onClone?: (profile: LlmProfile) => void;
  usage?: Record<string, ProfileUsage>;
  loadModels?: (profileId: string) => Promise<string[]>;
  presets?: BackendPreset[];
}

// ── Usage badge (pro renderBadges) ────────────────────────────────────────────

function UsageBadge({ usage }: { usage?: ProfileUsage }) {
  const { t } = useTranslation();
  if (!usage || usage.limit <= 0) return null;
  const pct = Math.min(100, Math.round((usage.today / usage.limit) * 100));
  const cls = pct >= 90
    ? 'text-red-400 bg-red-950/40'
    : pct >= 70
    ? 'text-yellow-400 bg-yellow-950/40'
    : 'text-green-400 bg-green-950/40';
  return (
    <span className={`text-[10px] px-1.5 py-0.5 rounded ${cls}`}>
      {t('profile.usageToday', '{{pct}}% dnes', { pct })}
    </span>
  );
}

// ── Editor (pro renderEditor) ─────────────────────────────────────────────────

function ProfileEditorInner({
  profile,
  update,
  presets,
  loadModels,
  usage,
  onClone,
}: {
  profile: LlmProfile;
  update: (patch: Partial<LlmProfile>) => void;
  presets: BackendPreset[];
  loadModels?: (profileId: string) => Promise<string[]>;
  usage?: ProfileUsage;
  onClone?: (profile: LlmProfile) => void;
}) {
  const { t } = useTranslation();
  const [showKey, setShowKey] = useState(false);
  const [models, setModels] = useState<string[]>([]);
  const [loadingM, setLoadingM] = useState(false);

  function applyPreset(id: string) {
    const pr = presets.find((x) => x.id === id);
    if (!pr || pr.id === 'custom') return;
    update({ id: pr.id, label: pr.label, url: pr.url, model: pr.model, backend: pr.backend });
  }

  async function handleLoadModels() {
    if (!loadModels) return;
    setLoadingM(true);
    try {
      const m = await loadModels(profile.id);
      if (!m.length) throw new Error(t('profile.noModels', 'Žádné modely'));
      setModels(m);
    } catch (e) {
      toast.error(t('profile.modelsError', 'Modely: {{message}}', { message: (e as Error).message }));
    } finally {
      setLoadingM(false);
    }
  }

  const pct = usage && usage.limit > 0
    ? Math.min(100, Math.round((usage.today / usage.limit) * 100))
    : null;

  if (profile.source === 'shared') {
    return (
      <div className='space-y-2'>
        <p className='text-xs text-gray-500'>
          {t(
            'profile.sharedReadonly',
            'Profil z hlavního seznamu (LLM Profiles modul) — read-only tady. Uprav ho',
          )}{' '}
          <a href='/modules/llm-profiles' className='text-blue-400 hover:underline'>
            {t('profile.inLlmProfiles', 'v LLM Profiles')}
          </a>.
        </p>
        <div className='grid grid-cols-2 gap-3 text-xs'>
          <div>
            <span className='text-gray-600'>Backend</span>
            <div className='font-mono text-gray-300'>{profile.backend}</div>
          </div>
          <div>
            <span className='text-gray-600'>Model</span>
            <div className='font-mono text-gray-300'>
              {profile.model || t('profile.defaultModel', '(výchozí)')}
            </div>
          </div>
          <div className='col-span-2'>
            <span className='text-gray-600'>URL</span>
            <div className='font-mono text-gray-300 truncate'>{profile.url}</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className='space-y-3'>
      {/* Usage progress bar */}
      {usage && usage.limit > 0 && (
        <div>
          <div className='h-1 bg-gray-800 rounded-full overflow-hidden'>
            <div
              className={`h-full rounded-full transition-all ${
                pct! >= 90 ? 'bg-red-500' : pct! >= 70 ? 'bg-yellow-500' : 'bg-blue-500'
              }`}
              style={{ width: `${pct}%` }}
            />
          </div>
          <div className='flex justify-between text-[10px] text-gray-600 mt-0.5'>
            <span>
              {t('profile.tokensUsed', '{{count}} tokenů', { count: usage.today.toLocaleString() })}
            </span>
            <span>
              {t('profile.tokenLimit', 'limit {{count}}', { count: usage.limit.toLocaleString() })}
            </span>
          </div>
        </div>
      )}

      {/* Preset */}
      <label className='block'>
        <span className='text-[11px] text-gray-500'>Preset</span>
        <Select
          className='mt-0.5'
          onChange={(e) => applyPreset(e.target.value)}
          defaultValue='custom'
        >
          {presets.map((pr) => <option key={pr.id} value={pr.id}>{pr.label}</option>)}
        </Select>
      </label>

      {/* ID + Label */}
      <div className='grid grid-cols-2 gap-3'>
        <label className='block'>
          <span className='text-[11px] text-gray-500'>ID</span>
          <input
            className='mt-0.5 w-full bg-gray-950 border border-gray-800 rounded-lg px-3 py-1.5 text-sm text-white font-mono focus:outline-none focus:border-blue-500/50'
            value={profile.id}
            onChange={(e) => update({ id: e.target.value })}
          />
        </label>
        <label className='block'>
          <span className='text-[11px] text-gray-500'>Label</span>
          <input
            className='mt-0.5 w-full bg-gray-950 border border-gray-800 rounded-lg px-3 py-1.5 text-sm text-white focus:outline-none focus:border-blue-500/50'
            value={profile.label}
            onChange={(e) => update({ label: e.target.value })}
          />
        </label>
      </div>

      {/* URL */}
      <label className='block'>
        <span className='text-[11px] text-gray-500'>API URL</span>
        <input
          className='mt-0.5 w-full bg-gray-950 border border-gray-800 rounded-lg px-3 py-1.5 text-sm text-white font-mono focus:outline-none focus:border-blue-500/50'
          value={profile.url}
          onChange={(e) => update({ url: e.target.value })}
          placeholder='http://host:port/v1'
        />
      </label>

      {/* Model */}
      <div>
        <div className='flex justify-between mb-0.5'>
          <span className='text-[11px] text-gray-500'>Model</span>
          {loadModels && (
            <Button variant='ghost' size='sm' onClick={handleLoadModels} loading={loadingM}>
              {t('profile.loadFromServer', 'Načíst ze serveru')}
            </Button>
          )}
        </div>
        {models.length > 0
          ? (
            <Select
              className='w-full'
              value={profile.model}
              onChange={(e) => update({ model: e.target.value })}
            >
              <option value=''>{t('profile.defaultOption', '— výchozí —')}</option>
              {models.map((m) => <option key={m} value={m}>{m}</option>)}
            </Select>
          )
          : (
            <input
              className='w-full bg-gray-950 border border-gray-800 rounded-lg px-3 py-1.5 text-sm text-white font-mono focus:outline-none focus:border-blue-500/50'
              value={profile.model}
              onChange={(e) => update({ model: e.target.value })}
              placeholder='model-name'
            />
          )}
      </div>

      {/* Backend + limit */}
      <div className='grid grid-cols-2 gap-3'>
        <label className='block'>
          <span className='text-[11px] text-gray-500'>Backend</span>
          <Select
            className='mt-0.5'
            value={profile.backend}
            onChange={(e) => update({ backend: e.target.value as 'openai' | 'anthropic' })}
          >
            <option value='openai'>openai (OpenAI-compatible)</option>
            <option value='anthropic'>anthropic (nativni API)</option>
          </Select>
        </label>
        <label className='block'>
          <span className='text-[11px] text-gray-500'>
            {t('profile.dailyTokenLimit', 'Denní limit tokenů')}
          </span>
          <input
            type='number'
            min={0}
            step={10000}
            className='mt-0.5 w-full bg-gray-950 border border-gray-800 rounded-lg px-3 py-1.5 text-sm text-white focus:outline-none focus:border-blue-500/50'
            value={profile.daily_token_limit}
            onChange={(e) => update({ daily_token_limit: parseInt(e.target.value) || 0 })}
          />
        </label>
      </div>

      {/* Reasoning toggle — Qwen think mode */}
      <label className='flex items-center gap-2 cursor-pointer'>
        <input
          type='checkbox'
          checked={profile.reasoning_enabled !== false}
          onChange={(e) => update({ reasoning_enabled: e.target.checked })}
          className='accent-blue-500'
        />
        <span className='text-xs text-gray-300'>
          Reasoning <span className='text-gray-600'>(chain-of-thought, Qwen think mode)</span>
        </span>
        <span className='text-[10px] text-gray-500 ml-auto'>
          {profile.reasoning_enabled === false
            ? t('profile.reasoningOff', 'vypnuto — rychlejší odpovědi bez reasoning_content')
            : t('profile.reasoningOn', 'zapnuto — model si může myslet před odpovědí')}
        </span>
      </label>

      {/* Klonovat profil */}
      {onClone && (
        <button
          type='button'
          onClick={() => onClone(profile)}
          className='w-full flex items-center justify-center gap-2 py-2 mt-1 border border-dashed border-gray-700 rounded-lg text-xs text-gray-500 hover:text-gray-300 hover:border-gray-500 transition-colors'
          title={t('profile.cloneTitle', 'Vytvoří kopii tohoto profilu s novým ID')}
        >
          📋 {t('profile.cloneProfile', 'Klonovat profil')}
        </button>
      )}

      {/* API key */}
      <label className='block'>
        <span className='text-[11px] text-gray-500'>
          API key {profile.api_key === '***' ? t('profile.keySaved', '(uložen)') : ''}
        </span>
        <div className='relative mt-0.5'>
          <input
            type={showKey ? 'text' : 'password'}
            className='w-full bg-gray-950 border border-gray-800 rounded-lg px-3 pr-9 py-1.5 text-sm text-white font-mono focus:outline-none focus:border-blue-500/50'
            value={profile.api_key === '***' ? '' : profile.api_key}
            placeholder={profile.api_key === '***' ? t('profile.keepEmpty', '(ponech prázdné)') : 'sk-...'}
            onChange={(e) => update({ api_key: e.target.value })}
          />
          <Button
            variant='ghost'
            size='icon'
            onClick={() => setShowKey((s) => !s)}
            className='absolute right-2.5 top-1/2 -translate-y-1/2'
          >
            {showKey ? <EyeOff size={13} /> : <Eye size={13} />}
          </Button>
        </div>
      </label>
    </div>
  );
}

// ── Main ──────────────────────────────────────────────────────────────────────

export function LlmProfileEditor({
  profiles,
  onSave,
  onDelete,
  onToggle,
  onReorder,
  onAdd,
  onClone,
  usage,
  loadModels,
  presets = DEFAULT_PRESETS,
}: LlmProfileEditorProps) {
  const { t } = useTranslation();
  const presetsShown = presets.map((pr) =>
    pr.id === 'custom' ? { ...pr, label: t('profile.customPreset', 'Vlastní...') } : pr
  );
  return (
    <PriorityList<LlmProfile>
      items={profiles}
      getTitle={(p) => p.label}
      getSubtitle={(p) => (
        <span className='font-mono'>
          {p.backend} · {p.model || '(no model)'}
        </span>
      )}
      renderEditor={(profile, update) => (
        <ProfileEditorInner
          profile={profile}
          update={update}
          presets={presetsShown}
          loadModels={loadModels}
          usage={usage?.[profile.id]}
          onClone={onClone}
        />
      )}
      renderBadges={(p) => (
        <>
          {p.source === 'shared' && (
            <span className='text-[10px] px-1.5 py-0.5 rounded text-blue-400 bg-blue-950/40'>
              {t('profile.mainList', 'hlavní seznam')}
            </span>
          )}
          <UsageBadge usage={usage?.[p.id]} />
        </>
      )}
      onSave={onSave}
      onDelete={onDelete}
      onToggle={onToggle}
      onReorder={onReorder}
      onAdd={onAdd}
      addLabel={t('chat.addProfile', 'Přidat profil')}
      emptyMessage={t('profile.emptyProfiles', 'Žádné LLM profily — přidej první')}
    />
  );
}
